
import Vue from 'vue';
import { BvTableFieldArray } from 'bootstrap-vue/src/components/table';

import ConfirmActionModal from '@/components/modal/ConfirmActionModal.vue';
import RuleEditorModal from '@/components/modal/RuleEditorModal.vue';
import Table from '@/components/common/Table.vue';
import { Zone } from '@/interfaces/lighting';
import permissions from '@/lib/permissions';
import {
	Rule,
	RuleAddEdit,
	RulespecDefault,
	RulespecLightingSettings,
	RulespecMovement,
	RulespecMovementSettings,
	RulespecRelativeLightSettings,
	RuleTable,
	RuleTemplate,
} from '@/interfaces/rules';
import { AxiosError } from 'axios';

interface RuleRow {
	id: number;
	name: string;
	ruleTable: RuleTable;
}

export default Vue.extend({
	components: {
		Table,
		RuleEditorModal,
		ConfirmActionModal,
	},

	data() {
		return {
			editSelectedRule: null,
			actionRuleId: null,
			actionRuleForm: null,
			usedZoneRules: [],
			removalRule: null,
		} as {
			editSelectedRule: RuleTemplate | null;
			actionRuleId: number | null;
			actionRuleForm: RuleAddEdit | null;
			usedZoneRules: Rule[];
			removalRule: RuleRow | null;
		};
	},

	computed: {
		fields(): BvTableFieldArray {
			return [
				{ key: 'name', label: this.$i18n.t('Name').toString(), sortable: true },
				{ key: 'ruleTable', label: this.$i18n.t('Rules').toString(), sortable: false },
				{ key: 'editDelete', label: '' },
			];
		},

		userCanRead(): boolean {
			return permissions.hasPermission(this.$store.getters['usermanagement/user/getRole'], [
				'rules',
				'read',
			]);
		},

		userCanWrite(): boolean {
			return permissions.hasPermission(this.$store.getters['usermanagement/user/getRole'], [
				'rules',
				'write',
			]);
		},

		rules(): RuleRow[] {
			const rules: RuleTemplate[] =
				this.$store.getters['activities/rules/getRuleTemplatesByType']('lighting');

			return rules.map((rule: RuleTemplate) => {
				return {
					id: rule.id,
					name: rule.name,
					ruleTable: {
						id: String(rule.id),
						activeRule: rule.rulespec[0].rules.filter(
							(rule): rule is RulespecMovement => rule.type === 'movement'
						)?.[0],
						standByRule: rule.rulespec[0].rules.filter(
							(rule): rule is RulespecMovement => rule.type === 'movement'
						)?.[1],
						lightingSettingsRule: rule.rulespec[0].rules.find(
							(rule): rule is RulespecLightingSettings => rule.type === 'lighting_settings'
						) ?? {
							type: 'lighting_settings',
							ramp_up_time: 10,
							ramp_down_time: 10,
						},
						inactiveRule: rule.rulespec[0].rules.find(
							(rule): rule is RulespecDefault => rule.type === 'default' || rule.type === 'dim'
						) as RulespecDefault,
					},
				};
			});
		},

		usedZoneRulesMin(): number {
			return Math.min(2, this.usedZoneRules.length);
		},
	},

	methods: {
		showModal(): void {
			this.editSelectedRule = null;
			this.$bvModal.show('modal-rule');
		},

		showModalEdit(rule: RuleRow): void {
			this.editSelectedRule = this.$store.getters['activities/rules/getRuleTemplate'](rule.id);
			this.$bvModal.show('modal-rule');
		},

		async createNewRuleTemplate(ruleForm: RuleAddEdit): Promise<void> {
			this.$store.dispatch('tools/loading/changeLoadingStatus');
			try {
				ruleForm.fk_client = this.$store.getters['tenancy/clients/getSelectedClient'].id;
				ruleForm.fk_site = this.$store.getters['tenancy/sites/getSelectedSite'].id;
				await this.$store.dispatch('activities/rules/addTemplate', { template: ruleForm });
				this.$bvModal.hide('modal-rule');
			} catch (error) {
				const err = error as AxiosError<{ message: string }>;
				this.$store.dispatch('tools/messageHandling/addErrorMessage', {
					id: 'error-new-rules',
					message: err.response && err.response!.data ? err.response!.data.message : err.message,
				});
			}
			this.$store.dispatch('tools/loading/changeLoadingStatus');
		},

		async prepareRuleTemplateEdit(id: number, ruleForm: RuleAddEdit): Promise<void> {
			await this.checkUsedZoneRules(id);
			this.actionRuleId = id;
			this.actionRuleForm = ruleForm;

			if (this.usedZoneRules.length > 0) {
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				(this.$refs.confirmRuleEditModal as any).show();
			} else {
				this.confirmEditRuleTemplate();
			}
		},

		async confirmEditRuleTemplate(): Promise<void> {
			this.$store.dispatch('tools/loading/changeLoadingStatus');
			try {
				await this.$store.dispatch('activities/rules/modifyTemplate', {
					id: this.actionRuleId,
					template: this.actionRuleForm,
				});
				let sensors: string[] = [];
				let movementSensors: string[] = [];
				let lightingSensor: string | null = null;
				if (this.usedZoneRules.length > 0) {
					for (const usedZoneRule of this.usedZoneRules) {
						const delta = usedZoneRule.rulespec[0].rules.some((rule) => rule.type === 'initial')
							? 1
							: 0;
						sensors = [];
						movementSensors = [];
						lightingSensor = null;
						if (usedZoneRule.rulespec[0].rules.length > 3 + delta) {
							movementSensors = (usedZoneRule.rulespec[0].rules[3] as RulespecMovementSettings)
								.sensors;
						}
						if (usedZoneRule.rulespec[0].rules.length === 3 + delta) {
							if ((usedZoneRule.rulespec[0].rules[0] as RulespecRelativeLightSettings).sensors) {
								lightingSensor = (
									usedZoneRule.rulespec[0].rules[0] as RulespecRelativeLightSettings
								).sensors[0];
							}
						}
						if (usedZoneRule.rulespec[0].rules.length === 6 + delta) {
							if (
								(usedZoneRule.rulespec[0].rules[5 + delta] as RulespecRelativeLightSettings).sensors
							) {
								lightingSensor = (
									usedZoneRule.rulespec[0].rules[5 + delta] as RulespecRelativeLightSettings
								).sensors[0];
							}
						}
						sensors = sensors.concat(movementSensors);
						if (lightingSensor != null) {
							sensors.push(lightingSensor);
						}
						await this.$store.dispatch('activities/rules/applyFromTemplate', {
							templateId: this.actionRuleId,
							sensors,
							movementSensors,
							lightingSensor,
							zoneId: usedZoneRule.fk_group,
						});
					}
				}
				this.cancelEditRuleTemplate();
				this.$bvModal.hide('modal-rule');
			} catch (error) {
				const err = error as AxiosError<{ message: string }>;
				this.$store.dispatch('tools/messageHandling/addErrorMessage', {
					id: 'error-edit-rules',
					message: err.response && err.response!.data ? err.response!.data.message : err.message,
				});
			}
			this.$store.dispatch('activities/rules/fetchRules');
			this.$store.dispatch('activities/appliedRules/fetchAppliedRules');
			this.$store.dispatch('tools/loading/changeLoadingStatus');
		},

		cancelEditRuleTemplate(): void {
			if (this.usedZoneRules.length > 0) {
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				(this.$refs.confirmRuleEditModal as any).hide();
			}
			this.usedZoneRules = [];
			this.actionRuleId = null;
			this.actionRuleForm = null;
		},

		async prepareRuleTemplateDeletion(row: RuleRow): Promise<void> {
			this.removalRule = row;
			await this.checkUsedZoneRules(row.id);

			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			(this.$refs.confirmRuleDeletionModal as any).show();
		},

		async confirmRuleTemplateDeletion(): Promise<void> {
			this.$store.dispatch('tools/loading/changeLoadingStatus');
			try {
				await this.$store.dispatch('activities/rules/deleteTemplateRule', {
					id: this.removalRule!.id,
				});
				this.cancelRuleTemplateDeletion();
			} catch (error) {
				const err = error as AxiosError<{ message: string }>;
				this.$store.dispatch('tools/messageHandling/addErrorMessage', {
					id: 'error-delete-rules',
					message: err.response && err.response!.data ? err.response!.data.message : err.message,
				});
			}
			this.$store.dispatch('tools/loading/changeLoadingStatus');
		},

		cancelRuleTemplateDeletion(): void {
			this.usedZoneRules = [];
			this.removalRule = null;

			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			(this.$refs.confirmRuleDeletionModal as any).hide();
		},

		async checkUsedZoneRules(id: number): Promise<void> {
			this.usedZoneRules = this.$store.getters['activities/rules/getRules'].filter(
				(rule: Rule) => rule.fk_rule_template === id
			);
		},

		fetchUsedRuleZone(id: number): Zone | null {
			return this.$store.getters['locations/zones/getZone'](id);
		},

		getZoneName(id: number): string {
			return this.fetchUsedRuleZone(id)?.name ?? this.$t('Unknown').toString();
		},
	},
});
